import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { settingsApi } from "@/api/settings";
import { Button } from "@/components/ui/Button";
import { Field, TextInput } from "@/components/ui/Field";
import { PageHeader } from "@/components/ui/PageHeader";
import { useToast } from "@/components/ui/Toast";

export function Tmdb() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [apiKey, setApiKey] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [testing, setTesting] = useState(false);

  const { data: status, isLoading } = useQuery({
    queryKey: ["settings", "tmdb"],
    queryFn: settingsApi.tmdb.get,
  });

  // Reset the input whenever the persisted state changes (e.g. after save).
  // Stored key isn't returned for security, so the input is always blank.
  useEffect(() => {
    setApiKey("");
  }, [status?.updated_at]);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!apiKey.trim()) {
      toast("Enter an API key first", "error");
      return;
    }
    setSubmitting(true);
    try {
      await settingsApi.tmdb.set(apiKey.trim());
      toast("TMDB API key saved", "success");
      qc.invalidateQueries({ queryKey: ["settings", "tmdb"] });
    } catch (err) {
      toast(err instanceof Error ? err.message : "Save failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    try {
      // If the user typed a new key, test that. Otherwise test what's stored.
      const res = await settingsApi.tmdb.test(apiKey.trim() || undefined);
      toast(
        res.ok ? "TMDB key is valid" : `Test failed: ${res.message ?? "unknown"}`,
        res.ok ? "success" : "error",
      );
    } catch (err) {
      toast(err instanceof Error ? err.message : "Test failed", "error");
    } finally {
      setTesting(false);
    }
  };

  const clearMut = useMutation({
    mutationFn: () => settingsApi.tmdb.clear(),
    onSuccess: () => {
      toast(
        "TMDB key cleared. Env-var fallback may still be active.",
        "info",
      );
      qc.invalidateQueries({ queryKey: ["settings", "tmdb"] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : "Clear failed", "error"),
  });

  const sourceBadge = (() => {
    if (!status?.configured) return <span className="text-foreground/40">not configured</span>;
    if (status.source === "db")
      return <span className="text-green-400">configured (database)</span>;
    if (status.source === "env")
      return (
        <span className="text-blue-300">
          configured (environment) — set a value below to override
        </span>
      );
    return null;
  })();

  return (
    <div className="space-y-6">
      <PageHeader
        title="TMDB"
        description="The Movie Database — used for movie + TV metadata lookup. Free API key from themoviedb.org."
      />

      <div className="rounded border border-line bg-surface/40 px-4 py-3 text-sm">
        <div className="text-xs uppercase tracking-wide text-foreground/50">
          Current status
        </div>
        <div className="mt-1">
          {isLoading ? <span className="text-foreground/60">Loading…</span> : sourceBadge}
        </div>
        {status?.updated_at && (
          <div className="mt-1 text-xs text-foreground/50">
            Last updated {new Date(status.updated_at).toLocaleString()}
          </div>
        )}
      </div>

      <form onSubmit={save} className="space-y-4">
        <Field
          label="API key"
          hint={
            status?.source === "db"
              ? "Stored value isn't shown. Type a new one to replace it."
              : "Get one at themoviedb.org → Settings → API."
          }
        >
          <TextInput
            type="password"
            autoComplete="off"
            value={apiKey}
            placeholder={
              status?.configured ? "leave blank to keep current value" : "your TMDB v3 API key"
            }
            onChange={(e) => setApiKey(e.target.value)}
          />
        </Field>

        <div className="flex items-center justify-between border-t border-line pt-3">
          <Button type="button" variant="secondary" onClick={runTest} loading={testing}>
            Test connection
          </Button>
          <div className="flex gap-2">
            {status?.source === "db" && (
              <Button
                type="button"
                variant="danger"
                onClick={() => {
                  if (
                    confirm(
                      "Remove the stored TMDB key? If TMDB_API_KEY is also set in the environment, it will take over.",
                    )
                  )
                    clearMut.mutate();
                }}
              >
                Clear
              </Button>
            )}
            <Button type="submit" loading={submitting}>
              Save
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
